import React, { useEffect, useState } from 'react'

// bootstrap
import { Col, Row, Button } from 'react-bootstrap';

// custom components //
import { CustomSpinner } from "_atoms";

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectJob } from '_redux/slices/utils/menu-context';
import { selectPassengerFlightList, selectGetPassengerFlightListStatus, getPassengerFlightList, resetPassengerFlightList } from '_redux/slices/timeline/flight-information-list';
import { selectSelectPassengersStatus, selectPassengers, resetSelectPassengersFlags, selectDuplicatedArrivalsBookings, setDuplicatedArrivalsBookings } from '_redux/slices/timeline/timeline-data';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';

// utils
import { Colors, Spacing } from '_styles'
import { getAssistanceString, processName } from '_utils/functions';
import { selectUsedTheme } from '_redux/slices/timeline/theme';


const PassengerRow = ({ passenger, itemKey, selected, setSelected, taskType }) => {

    const [canSelect, setCanSelect] = useState(true);
    const usedTheme = useSelector(selectUsedTheme);

    useEffect(() => {
        // if taskType is arrival 
        if (taskType === "arrival") {
            //if passengerStatus is collected and _id does not include -part(not duplicated), set canSelect to false
            if ((passenger?.selectArrivalPaxStatus == 'collected') && !passenger?._id?.includes("-part")) {
                setCanSelect(false);
            }
            // if passengerStatus is collected and _id includes -part(duplicated) and there is no duplicateCount(the duplication has been selected), set canSelect to false
            if ((passenger?.selectArrivalPaxStatus == 'collected') && passenger?._id?.includes("-part") && !passenger?.duplicateCount) {
                setCanSelect(false);
            }
        }
        // if taskType is combinedRampBooking and rampPaxCollected , set canSelect to false
        else if (taskType === "combinedRampBooking" && passenger?.selectRampPaxStatus == 'collected') setCanSelect(false);
        // if taskType is combinedBooking and passengerStatus is boarded, set canSelect to false
        else if (taskType === "combinedBooking" && passenger?.selectBoardingPaxStatus == 'boarded') setCanSelect(false);
    }, [passenger])

    //#region functions

    const handleRadioPress = () => {
        if (!canSelect) return;

        // check if current row is already selected
        let tempSelected = Array.isFullArray(selected) ? [...selected] : [];

        // check if the selected array includes the current passenger or not, if yes remove it, otherwise add it to the selected array
        if (tempSelected?.includes(passenger?._id)) {
            tempSelected = tempSelected.filter(id => id != passenger?._id);
        } else {
            tempSelected.push(passenger?._id);
        }
        setSelected(tempSelected);
    };

    //#endregion

    return (
        <Row className="p-2 m-0" style={{ border: `1px solid ${Colors.PRM_LIGHT_BLUE}`, borderRadius: "5px", opacity: canSelect ? 1 : 0.5 }} key={itemKey}>
            <Col xs={10} className="d-flex gap-2">
                <div style={{ border: `1px solid ${Colors.PRM_LIGHT_BLUE}`, heigh: "100%", width: "1px" }} />
                <Col>
                    <p className="m-0" style={{ fontSize: Spacing.SCALE_18, color: usedTheme.TEXT_COLOR }}>{processName(passenger?.name)}</p>
                    <p className="m-0" style={{ fontSize: Spacing.SCALE_14, color: Colors.PRM_LIGHT_BLUE }}>{getAssistanceString(passenger?.assistance)}</p>
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ width: Spacing.SCALE_6, height: Spacing.SCALE_6, backgroundColor: canSelect ? "red" : "green", borderRadius: Spacing.SCALE_12 }} />

                        <p className="m-0" style={{ fontSize: Spacing.SCALE_14, color: usedTheme.TEXT_COLOR }}>{canSelect ? "Waiting for service" : passenger?.selectBoardingPaxStatus === 'boarded' ? 'Boarded' : "Collected"}</p>
                    </div>

                </Col>
            </Col>

            <Col xs={2} className="pt-2">
                <div onClick={handleRadioPress} style={{ cursor: canSelect ? "pointer" : "not-allowed" }}>
                    <div style={{ height: Spacing.SCALE_22, width: Spacing.SCALE_22, borderRadius: Spacing.SCALE_12, alignItems: 'center', justifyContent: 'center', display: "flex", border: `2px solid ${Colors.PRM_LIGHT_BLUE}`, marginTop: Spacing.SCALE_4 }}>
                        {
                            (selected?.includes(passenger?._id) || !canSelect) ?
                                <div style={{ height: Spacing.SCALE_12, width: Spacing.SCALE_12, borderRadius: 6, backgroundColor: Colors.PRM_LIGHT_BLUE, }} />
                                : null
                        }

                    </div>
                </div>
            </Col>

        </Row>
    )

}

const FlightList = ({ onClose }) => {

    //#region redux

    const dispatch = useDispatch();

    const job = useSelector(selectJob);
    const passengerFlightList = useSelector(selectPassengerFlightList);                             // selected flight passenger list 
    const selectPassengersStatus = useSelector(selectSelectPassengersStatus);
    const duplicatedArrivalsBookings = useSelector(selectDuplicatedArrivalsBookings);
    const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user

    //#endregion

    //#region local states

    const [selected, setSelected] = useState([]);
    const [taskType, setTaskType] = useState("");                             // arrival, combined, combinedRamp
    const [PAXList, setPAXList] = useState([]);

    //#endregion

    //#region useEffect

    useEffect(() => {
        dispatch(resetPassengerFlightList());
        dispatch(resetSelectPassengersFlags());
        setPAXList([]);

        if (job?.flightInformation) {

            const flightNumber = job?.flightInformation?.flightNumber;
            const departureDate = job?.flightInformation?.flightDate;
            const iata = job?.combinedBooking ? job?.flightInformation?.departureAirport : job?.flightInformation?.arrivalAirport;
            const flightType = (job?.flightBooking || job?.combinedRampBooking) ? "inbound" : "outbound"
            if (flightNumber && departureDate && iata) {
                // need to get passenger flight list for this flight
                dispatch(getPassengerFlightList({
                    flightNumber: flightNumber,
                    departureDate: departureDate,
                    iata: iata,
                    type: flightType
                }))
            }
        }
    }, []);

    useEffect(() => {
        if (Array.isFullArray(passengerFlightList)) {
            let tempPaxArray = [];
            passengerFlightList?.forEach((pax) => {
                tempPaxArray.push(pax)
                if (pax?.duplicateCount) createDuplicates(pax, tempPaxArray)

            });
            setPAXList(tempPaxArray)
        } else setPAXList([]);

    }, [passengerFlightList])

    useEffect(() => {
        // set task type 
        if (job?.combinedBooking) setTaskType("combinedBooking");
        else if (job?.combinedRampBooking) setTaskType("combinedRampBooking");
        else if (job?.flightBooking && !job?.combinedRampBooking) setTaskType("arrival");
    }, [job]);

    useEffect(() => {
        if (selectPassengersStatus == "succeeded") {
            dispatch(resetSelectPassengersFlags());
            onClose();
        };
    }, [selectPassengersStatus]);

    //#endregion

    //#region functions

    const handlePassengerSelection = () => {
        // remove the selected duplicated arrival booking from the duplicatedArrivalsBookings array
        let tempDuplicatedArrivals = Array.isFullArray(duplicatedArrivalsBookings)
            ? duplicatedArrivalsBookings?.filter((duplicate) => !selected?.includes(duplicate?.doc?._id))
            : [];
        dispatch(setDuplicatedArrivalsBookings([...tempDuplicatedArrivals]));

        const payload = {
            taskId: job?._id,
            body: {
                bookingsIds: selected,
                taskType,
                iata: dbUser.details.authority

            }

        };
        dispatch(selectPassengers(payload));
    };

    function createDuplicates(obj, array) {
        const duplicateCount = obj?.duplicateCount;

        if (duplicateCount) {
            for (let i = 0; i < duplicateCount; i++) {
                let newObj = {
                    ...obj,
                    _id: obj?._id + "-part" + (i + 1),
                    status: "user-confirmed",                                       // set to user confirmed status
                    name: obj?.name + " " + "PART-" + (i + 1),    // set name to include part number
                    duplicateVersion: i
                }; // clone the object
                array.push(newObj)
            }
        }
    }

    //#endregion

    return (
        <Row className="p-3" >
            <Col xs={12} className="p-0">
                <Col xs={12} className="gap-3 d-flex flex-column" style={{ maxHeight: "400px", overflowY: "scroll", overflowX: "unset" }}>
                    {Array.isFullArray((PAXList)) ?
                        PAXList?.map((passenger, index) => <PassengerRow passenger={passenger} itemKey={index} setSelected={setSelected} selected={selected} taskType={taskType} />)

                        : null}
                </Col>
                <Col xs={12} className="d-flex justify-content-end mt-4" >

                    <Button variant="outline-primary" onClick={handlePassengerSelection} disabled={!Array.isFullArray(selected)} >
                        {selectPassengersStatus == "loading" ? <CustomSpinner /> : "Select Passenger"}
                    </Button>
                </Col>
            </Col>
        </Row>
    )
}

export default FlightList